import { Injectable } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import {
  WorkspaceAvatarUpdatedEvent,
  WorkspaceCoverUpdatedEvent,
  HeaderInfo,
  User,
} from '../../../../../shared/interfaces';
import { EmitterService } from '../../../../../shared/services/emitter.service';
import { AuthService } from '../../../../../shared/services/auth.service';
import { ApiService } from '../../../../../shared/services/api.service';
import { UploadService } from '../../../../../shared/services/upload.service';
import { ThemeService } from '../../../../../shared/services/theme.service';
import { WorkspaceService } from '../../../../../shared/services/workspace.service';
import {
  AvatarImageResult,
  SetAvatarImageOnModalParams,
  SetCoverImageParams,
} from '../../../../../workspace/models/workspace.model';
import { GeneralUtilsService } from '../../../../../shared/services/general-utils.service';

@Injectable({ providedIn: 'root' })
export class WorkspacePermissionsImgUploadService {
  constructor(
    private readonly authService: AuthService,
    private readonly apiService: ApiService,
    private readonly uploadService: UploadService,
    private readonly themeService: ThemeService,
    private readonly workspaceService: WorkspaceService,
    private readonly generalUtilsService: GeneralUtilsService
  ) {}

  public setAvatarImageOnModal(params: SetAvatarImageOnModalParams): Promise<AvatarImageResult> {
    return new Promise((resolve) => {
      params.uploadModal.result.then(
        (result: string) => {
          if (!result) return;

          const authUser: User = this.authService.getAuthUser();
          const workspaceUrl: string = `${this.apiService.baseUrl}/api/workspace/${authUser.company.id}/${params.workspace.id}/avatar`;
          this.uploadService
            .uploadImage(result, workspaceUrl)
            .pipe(takeUntil(params.ngUnsubscribe))
            .subscribe((data) => {
              if (!data?.avatarUrl) return;

              const avatarUrl: string = this.generalUtilsService.getCacheBusterUrl(data.avatarUrl);
              const headerUpdate: Partial<HeaderInfo> = { avatarImage: avatarUrl };
              this.themeService.updateHeaderInfo(headerUpdate);

              EmitterService.get(WorkspaceAvatarUpdatedEvent).emit({
                workspaceId: params.workspace.id,
                avatarUrl,
              });

              this.workspaceService.setActiveWorkspaceCoverAndAvatar({ avatar: avatarUrl });

              resolve({ avatarUrl, workspaceAvatarUrl: avatarUrl });
            });
        },
        () => {}
      );
    });
  }

  setCoverImage(params: SetCoverImageParams): Promise<string> {
    return new Promise((resolve) => {
      if (!params.result) return resolve(params.workspace.coverUrl || '');

      const authUser = this.authService.getAuthUser();
      let coverUrl = params.result;

      if (params.workspace.coverUrl) {
        coverUrl = coverUrl + '?v=' + params.cacheBuster;
      }

      const workspaceUrl = `${this.apiService.baseUrl}/api/workspace/${authUser.company.id}/${params.workspace.id}/cover`;

      this.uploadService
        .uploadImage(params.result, workspaceUrl)
        .pipe(takeUntil(params.ngUnsubscribe))
        .subscribe((data: any) => {
          if (!data?.coverUrl) return resolve(coverUrl);

          coverUrl = this.generalUtilsService.getCacheBusterUrl(data.coverUrl);
          params.workspace.coverUrl = coverUrl;

          this.workspaceService.setActiveWorkspaceCoverAndAvatar({ cover: coverUrl });
          EmitterService.get(WorkspaceCoverUpdatedEvent).emit(coverUrl);

          resolve(coverUrl);
        });
    });
  }
}
